import { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useHtzSummaryBaseDataDeleteMutation } from "@/services/backend/htz/summary/api/base-data-api";
import {
  SummaryBaseData,
  htzSummaryBaseDataKey,
} from "@/services/backend/htz/summary/types/base-data";
import { useIsDefaultBaseData } from "@/services/backend/shared/base-data-kit/components/default-base-data-checkbox";

export function DeleteSummaryBaseDataDialog({
  baseData,
  onDeleted,
}: {
  baseData: SummaryBaseData;
  onDeleted?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [deleteBaseData, { isLoading, error, reset }] =
    useHtzSummaryBaseDataDeleteMutation();

  // The backend refuses to delete the instance a company instantiates
  // from, so the dialog says so instead of sending a request that will
  // be rejected.
  const { isDefault } = useIsDefaultBaseData({
    subdomain: "htz",
    dataKey: htzSummaryBaseDataKey,
    companyId: baseData.companyId,
    id: baseData.id,
  });

  const onConfirm = () => {
    if (isLoading) {
      return;
    }
    deleteBaseData({ id: baseData.id })
      .unwrap()
      .then(() => {
        toast.success(t("Basisdaten gelöscht"));
        reset();
        setOpen(false);
        onDeleted?.();
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger icon={Trash2} label="" variant="destructive" />
      <DialogContent>
        <CommonDialogHeader
          title={t("Basisdaten wirklich löschen?")}
          description={
            isDefault
              ? t(
                  "Diese Basisdaten sind als aktiv markiert und können nicht gelöscht werden. Markieren Sie zuerst andere Basisdaten als aktiv.",
                )
              : t(
                  "Die Basisdaten werden gelöscht. Bereits erstellte Inspektionsberichte bleiben unverändert.",
                )
          }
        />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={onConfirm}
          actionLabel={t("Löschen")}
          destructive
          disabled={isDefault}
        />
      </DialogContent>
    </Dialog>
  );
}
